import { bareApi } from './bareApi';
import { getAccountStateReason } from './accountState';

/**
 * The in-flight refresh, if there is one.
 *
 * Concurrent 401s must not each fire their own refresh: a dashboard issues
 * several requests in parallel, so one expired token produces several 401s at
 * once, and with rotating refresh tokens the first call invalidates the token
 * the others are still using — so all but one fail and force a sign-out that was
 * never necessary. Every caller awaits the same promise instead.
 */
let refreshPromise: Promise<unknown> | null = null;

const REFRESH_LOCK = 'myomnia-refresh-cookie';
const REFRESH_GENERATION = 'myomnia-refresh-generation';
let localGeneration = '';

export const readRefreshGeneration = (): string => {
  try {
    return localStorage.getItem(REFRESH_GENERATION) ?? '';
  } catch {
    return localGeneration;
  }
};

const recordRefresh = (outcome: 'ok' | 'rejected' | 'unavailable') => {
  localGeneration = `${outcome}:${crypto.randomUUID()}`;
  try {
    localStorage.setItem(REFRESH_GENERATION, localGeneration);
  } catch {
    // The in-page promise still protects this tab when storage is unavailable.
  }
};

const runRefresh = async (observedGeneration: string): Promise<unknown> => {
  const refresh = async () => {
    // Another tab refreshed the shared cookie while this request was in flight.
    const currentGeneration = readRefreshGeneration();
    if (currentGeneration !== observedGeneration) {
      if (currentGeneration.startsWith('rejected:')) throw { response: { status: 401 } };
      if (currentGeneration.startsWith('unavailable:')) throw new Error('Refresh was unavailable in another tab');
      return;
    }
    let response: unknown;
    for (let attempt = 0; ; attempt += 1) {
      try {
        response = await bareApi.post('/v1/user/auth/refresh');
        break;
      } catch (error) {
        const status = (error as { response?: { status?: number } }).response?.status;
        if (status !== 503 || getAccountStateReason(error) !== 'ACCOUNT_STATE_UNAVAILABLE' || attempt >= 3) {
          recordRefresh(typeof status === 'number' && status >= 400 && status < 500 ? 'rejected' : 'unavailable');
          throw error;
        }
        await new Promise<void>((resolve) => setTimeout(resolve, 300 * 2 ** attempt));
      }
    }
    recordRefresh('ok');
    return response;
  };

  return navigator.locks?.request ? navigator.locks.request(REFRESH_LOCK, refresh) : refresh();
};

export const refreshSession = (observedGeneration = readRefreshGeneration()): Promise<unknown> =>
  (refreshPromise ??= runRefresh(observedGeneration).finally(() => {
    refreshPromise = null;
  }));
